/**
 * Numbers, money, dates and hours, in the firm's own conventions.
 *
 * From the tenant rather than from the browser. A dispatch workstation is whatever machine
 * the office had spare, set to whatever locale somebody chose when they installed it, and a
 * report a firm settles up against must not read differently on two desks in the same room.
 * The firm's culture, currency and timezone all arrive with its branding at sign-in.
 */

import { useMemo } from 'react';

import { useAuth } from '../auth/authContext';

export interface TenantFormats {
  /** An amount with the firm's currency symbol. */
  money: (amount: number | null | undefined) => string;
  /** An amount with no symbol, for a column whose header names the currency. */
  amount: (amount: number | null | undefined) => string;
  /** A whole number. */
  count: (value: number) => string;
  /** A number to at most two places — a rate, a distance — with the firm's decimal sign. */
  decimal: (value: number) => string;
  /** Minutes, as hours and minutes: `7:45`. */
  hours: (minutes: number) => string;
  /** A `yyyy-MM-dd` local day, short. */
  day: (date: string) => string;
  /** A `yyyy-MM-dd` local day, with its weekday. */
  dayLong: (date: string) => string;
  /** An instant, shown as a time in the firm's timezone. */
  time: (iso: string) => string;
  /**
   * The same, with seconds.
   *
   * The order history is what needs them: an offer and its refusal are two events, and at
   * minute precision they are the same moment.
   */
  timeWithSeconds: (iso: string) => string;
  /** An instant, shown as a date and time in the firm's timezone. */
  moment: (iso: string) => string;
  /** A weekday number, Sunday being zero, in the firm's language. */
  weekday: (dayOfWeek: number) => string;
  /** Kilometres from metres. */
  distance: (metres: number) => string;
  /** A share of one, as a percentage. */
  percent: (share: number | null) => string;
  /** Seconds, as minutes and seconds. */
  duration: (seconds: number | null) => string;
  currency: string;
  timeZone: string;
}

/**
 * What the formatters use for the moment before the firm's own branding has loaded.
 *
 * Nothing with an amount on it is drawn in that moment, so the currency here is never seen;
 * it is EUR rather than BGN because the leva left circulation with Bulgaria's switch to the
 * euro, and a fallback that names a dead currency is one somebody copies as a default (#376).
 */
const FALLBACK = { culture: 'bg-BG', currency: 'EUR', timeZone: 'Europe/Sofia' };

/**
 * The formatters for the firm currently signed in.
 *
 * Memoised because `Intl` constructors are not cheap and a grid renders a few thousand
 * cells; building one per cell was measurably slower than building six per render.
 */
export function useTenantFormats(): TenantFormats {
  const { branding } = useAuth();

  const culture = branding?.defaultCulture ?? FALLBACK.culture;
  const currency = branding?.defaultCurrency ?? FALLBACK.currency;
  const timeZone = branding?.timeZoneId ?? FALLBACK.timeZone;

  return useMemo(() => {
    const money = new Intl.NumberFormat(culture, { style: 'currency', currency });
    const amount = new Intl.NumberFormat(culture, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const count = new Intl.NumberFormat(culture, { maximumFractionDigits: 0 });
    const decimal = new Intl.NumberFormat(culture, { maximumFractionDigits: 2 });
    const km = new Intl.NumberFormat(culture, { maximumFractionDigits: 1 });
    const share = new Intl.NumberFormat(culture, { style: 'percent', maximumFractionDigits: 0 });

    // The two day formatters read UTC deliberately. Their input is already a local date in
    // the firm's own calendar, not an instant, and converting it a second time would shift
    // it by a day at some offset somewhere.
    const day = new Intl.DateTimeFormat(culture, {
      day: '2-digit',
      month: 'short',
      timeZone: 'UTC',
    });

    const dayLong = new Intl.DateTimeFormat(culture, {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      timeZone: 'UTC',
    });
    const time = new Intl.DateTimeFormat(culture, { timeStyle: 'short', timeZone });
    const timeWithSeconds = new Intl.DateTimeFormat(culture, { timeStyle: 'medium', timeZone });
    const moment = new Intl.DateTimeFormat(culture, {
      dateStyle: 'short',
      timeStyle: 'short',
      timeZone,
    });
    const weekday = new Intl.DateTimeFormat(culture, { weekday: 'long', timeZone: 'UTC' });

    /** A local day, read as a day rather than as an instant. */
    const asDay = (date: string) => new Date(`${date}T00:00:00Z`);

    return {
      money: (value) => (value === null || value === undefined ? '' : money.format(value)),
      amount: (value) => (value === null || value === undefined ? '' : amount.format(value)),
      count: (value) => count.format(value),
      decimal: (value) => decimal.format(value),

      hours: (minutes) => {
        const whole = Math.floor(Math.abs(minutes) / 60);
        const rest = Math.abs(minutes) % 60;

        return `${minutes < 0 ? '-' : ''}${whole}:${String(rest).padStart(2, '0')}`;
      },

      day: (date) => day.format(asDay(date)),
      dayLong: (date) => dayLong.format(asDay(date)),
      time: (iso) => time.format(new Date(iso)),
      timeWithSeconds: (iso) => timeWithSeconds.format(new Date(iso)),
      moment: (iso) => moment.format(new Date(iso)),

      // Any Sunday will do; only the weekday name is read off it.
      weekday: (dayOfWeek) => weekday.format(new Date(Date.UTC(2024, 0, 7 + dayOfWeek))),

      distance: (metres) => `${km.format(metres / 1000)} km`,
      percent: (value) => (value === null ? '—' : share.format(value)),

      duration: (seconds) => {
        if (seconds === null) return '—';

        const minutes = Math.floor(seconds / 60);

        return minutes === 0
          ? `${count.format(seconds)}s`
          : `${count.format(minutes)}m ${String(seconds % 60).padStart(2, '0')}s`;
      },

      currency,
      timeZone,
    };
  }, [culture, currency, timeZone]);
}
